import { useState } from "react";
import axios from "axios";
import { Send, Loader2, CheckCircle2, X, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ──────────────────────────────────────────────────────────────────────────
 * SubmitStory
 *
 * Public "Send us your story" CTA. Renders a warm, inviting section with a
 * single button that opens an inline modal form. Submissions POST to
 * /api/submit-story which stores them in MongoDB for the studio owner to
 * review at their own pace.
 *
 * Intentionally low-friction: only Name + Email + Story title + Message are
 * required. Source URL & category are optional hints. No login wall.
 * ────────────────────────────────────────────────────────────────────────── */
export default function SubmitStory() {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: "", email: "", title: "", message: "", source_url: "", category: "",
  });

  const onChange = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.message.trim().length < 10) {
      setErr("Tell us a little more — at least a couple of sentences.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/submit-story`, {
        name: form.name.trim(),
        email: form.email.trim(),
        title: form.title.trim(),
        message: form.message.trim(),
        source_url: form.source_url.trim() || null,
        category: form.category || null,
      });
      setDone(true);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || ex.message || "Couldn't send — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setOpen(false);
    // reset after the close animation so the next opening is clean
    setTimeout(() => {
      setDone(false); setErr(""); setSubmitting(false);
      setForm({ name: "", email: "", title: "", message: "", source_url: "", category: "" });
    }, 300);
  };

  return (
    <>
      <section className="px-6 md:px-10 mt-16 md:mt-24" data-testid="submit-story-section">
        <div className="max-w-[1200px] mx-auto">
          <div
            className="relative overflow-hidden rounded-2xl p-8 md:p-12"
            style={{
              background: "linear-gradient(135deg, rgba(245, 158, 11, 0.10), rgba(16, 185, 129, 0.08) 60%, rgba(0,0,0,0))",
              border: "1px solid rgba(245, 158, 11, 0.25)",
            }}
          >
            {/* soft glow accent in the corner */}
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-30 pointer-events-none"
                 style={{ background: "radial-gradient(closest-side, rgba(245,158,11,0.4), transparent)" }} />

            <div className="relative max-w-3xl">
              <div className="text-[11px] tracking-[0.28em] uppercase text-[color:var(--accent)] mb-4 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" /> Got a story?
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-tight mb-5">
                Send us your story.
              </h2>
              <p className="text-[color:var(--muted)] leading-relaxed text-base md:text-lg mb-3">
                Whether it's an animal rescue or a personal journey — if it deserves to be heard,
                we'll feature it, help you raise funds, and give you valuable exposure.
              </p>
              <p className="text-[color:var(--muted)] leading-relaxed text-base md:text-lg mb-6">
                Keep us updated on your progress and successes too — it's all about getting your
                voice out there and bringing a global community behind you.
              </p>
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="submit-story-open"
              >
                <Send className="w-4 h-4" /> Share your story
              </button>
              <p className="text-xs text-[color:var(--muted)] mt-3">
                Free · we read every submission · we'll reply within a few days.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Modal form */}
      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm overflow-y-auto p-4"
          onClick={close}
          data-testid="submit-story-modal"
        >
          <div
            className="panel w-full max-w-lg mx-auto my-12 md:my-6 p-6 md:p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-3 right-3 text-[color:var(--muted)] hover:text-white transition-colors p-1.5"
              data-testid="submit-story-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {done ? (
              <div className="text-center py-8" data-testid="submit-story-success">
                <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4"
                     style={{ background: "rgba(16, 185, 129, 0.15)" }}>
                  <CheckCircle2 className="w-7 h-7" style={{ color: "#10b981" }} />
                </div>
                <h3 className="font-display text-2xl mb-2">Thanks — your story is in.</h3>
                <p className="text-[color:var(--muted)] text-sm mb-6">
                  We read every submission and will be in touch within a few days at <strong className="text-white">{form.email || "the email you provided"}</strong>.
                </p>
                <button onClick={close} className="btn-primary" data-testid="submit-story-success-close">
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <h3 className="font-display text-2xl">Share your story</h3>
                  <p className="text-xs text-[color:var(--muted)] mt-1">
                    The more colour you give us, the easier it is to bring your story to life.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[11px] text-[color:var(--muted)] mb-1 block">Your name</span>
                    <input
                      type="text"
                      required
                      maxLength={120}
                      className="input-base w-full px-3 py-2 text-sm"
                      value={form.name}
                      onChange={onChange("name")}
                      data-testid="submit-story-name"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] text-[color:var(--muted)] mb-1 block">Email</span>
                    <input
                      type="email"
                      required
                      maxLength={200}
                      className="input-base w-full px-3 py-2 text-sm"
                      value={form.email}
                      onChange={onChange("email")}
                      data-testid="submit-story-email"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="text-[11px] text-[color:var(--muted)] mb-1 block">Story title</span>
                  <input
                    type="text"
                    required
                    maxLength={200}
                    placeholder="e.g. Rescuing Atila"
                    className="input-base w-full px-3 py-2 text-sm"
                    value={form.title}
                    onChange={onChange("title")}
                    data-testid="submit-story-title"
                  />
                </label>

                <label className="block">
                  <span className="text-[11px] text-[color:var(--muted)] mb-1 block">Tell us about it</span>
                  <textarea
                    required
                    minLength={10}
                    maxLength={4000}
                    rows={5}
                    placeholder="What happened, who's involved, why it matters, where things stand now…"
                    className="input-base w-full px-3 py-2 text-sm leading-relaxed resize-y"
                    value={form.message}
                    onChange={onChange("message")}
                    data-testid="submit-story-message"
                  />
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[11px] text-[color:var(--muted)] mb-1 block">Category (optional)</span>
                    <select
                      className="input-base w-full px-3 py-2 text-sm"
                      value={form.category}
                      onChange={onChange("category")}
                      data-testid="submit-story-category"
                    >
                      <option value="">Choose…</option>
                      <option value="animal_rescue">Animal Rescue</option>
                      <option value="charity">Charity</option>
                      <option value="social_impact">Social Impact / Personal Journey</option>
                      <option value="environment">Environment</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-[11px] text-[color:var(--muted)] mb-1 block">Link (optional)</span>
                    <input
                      type="url"
                      maxLength={600}
                      placeholder="GoFundMe / Instagram / website"
                      className="input-base w-full px-3 py-2 text-sm"
                      value={form.source_url}
                      onChange={onChange("source_url")}
                      data-testid="submit-story-url"
                    />
                  </label>
                </div>

                {err && <p className="text-xs text-rose-400" data-testid="submit-story-error">{err}</p>}

                <div className="flex items-center justify-between gap-2 pt-2">
                  <p className="text-[11px] text-[color:var(--muted)] leading-relaxed">
                    By submitting you agree to be contacted about your story.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
                    data-testid="submit-story-submit"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
